import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { describeSegment } from "./SegmentCriteria";
import {
  ActionsCell,
  Badge,
  EmptyState,
  IconButton,
  Pagination,
  TableSkeleton,
} from "../../../components/ui";
import {
  apiMessage,
  cancelNotification,
  deleteNotification,
  getNotifications,
} from "../../../services/notifications";

export const STATUSES = {
  scheduled: { label: "Scheduled", color: "blue" },
  sending: { label: "Sending", color: "amber" },
  spreading: { label: "Sending in waves", color: "blue" },
  sent: { label: "Sent", color: "green" },
  partial: { label: "Partly sent", color: "amber" },
  failed: { label: "Failed", color: "red" },
  canceled: { label: "Canceled", color: "slate" },
};

export const KINDS = {
  simple: { label: "Simple", icon: "ri-message-3-line" },
  video: { label: "Video", icon: "ri-film-line" },
  missed_videos: { label: "Missed videos", icon: "ri-file-list-3-line" },
  quiz_reminder: { label: "Quiz reminder", icon: "ri-trophy-line" },
  app_update: { label: "App update", icon: "ri-download-cloud-line" },
};

const SOURCES = {
  manual: "Manual",
  publish: "Video published",
  automation: "Automatic",
};

/** Who received a notification, in a few words. */
export function audienceLabel(audience, t) {
  if (!audience || audience.type === "all") return t("All users");
  if (audience.type === "users") {
    const users = audience.userIds || [];
    const names = users
      .filter((u) => u && typeof u === "object")
      .map((u) => [u.name, u.last_name].filter(Boolean).join(" ") || u.email);
    if (names.length === 1) return names[0];
    return t("{{count}} users", { count: users.length });
  }
  return describeSegment(audience.segment || {}, t);
}

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

const FILTERS = [
  { value: "", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "spreading", label: "Sending in waves" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
];

const History = ({ refreshKey, onDuplicate, onChange }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null); // { action: "cancel" | "delete", notification }

  const load = useCallback(() => {
    getNotifications({ page, status }).then(setData);
  }, [page, status]);

  useEffect(() => {
    setData(null);
    load();
  }, [load, refreshKey]);

  // Sends in progress: refresh until they finish
  useEffect(() => {
    if (!data?.items?.some((n) => ["sending", "spreading"].includes(n.status))) return undefined;
    // Waves leave every 15 minutes: a slower refresh is enough
    const spreading = data.items.some((n) => n.status === "spreading") && !data.items.some((n) => n.status === "sending");
    const timer = setInterval(load, spreading ? 30000 : 5000);
    return () => clearInterval(timer);
  }, [data, load]);

  const run = async ({ action, notification }) => {
    try {
      if (action === "cancel") {
        await cancelNotification(notification._id);
        toast.success(t("Notification canceled"));
      } else {
        await deleteNotification(notification._id);
        toast.success(t("Removed from history"));
      }
      load();
      onChange && onChange();
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
  };

  const items = data?.items || [];

  return (
    <section className="ui-card">
      <div className="ui-toolbar">
        <div className="ui-chips">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className="ui-chip"
              aria-pressed={status === f.value}
              onClick={() => {
                setStatus(f.value);
                setPage(1);
              }}
            >
              {t(f.label)}
            </button>
          ))}
        </div>
        <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />
      </div>

      {!data ? (
        <TableSkeleton />
      ) : !items.length ? (
        <EmptyState icon="ri-notification-off-line">{t("No notification yet")}</EmptyState>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>{t("Notification")}</th>
                <th>{t("Recipients")}</th>
                <th>{t("Status")}</th>
                <th>{t("Result")}</th>
                <th>{t("Date")}</th>
                <th aria-label={t("Actions")}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => {
                const kind = KINDS[n.kind] || KINDS.simple;
                const state = STATUSES[n.status] || STATUSES.sent;
                const image = n.imageUrl || n.video?.thumbnailUrl;
                return (
                  <tr key={n._id}>
                    <td>
                      <div className="ui-media notif-history-media">
                        {image ? (
                          <img className="ui-media-thumb" src={image} alt="" loading="lazy" />
                        ) : (
                          <span className="ui-media-thumb notif-history-icon" aria-hidden="true">
                            <i className={kind.icon}></i>
                          </span>
                        )}
                        <div className="notif-history-text">
                          <div className="ui-media-heading">
                            <span className="ui-media-title">{n.title}</span>
                            <Badge color={n.kind === "video" ? "violet" : n.kind === "missed_videos" ? "amber" : "slate"}>
                              {t(kind.label)}
                            </Badge>
                          </div>
                          <span className="notif-history-body">{n.body}</span>
                          <span className="ui-media-meta">{t(SOURCES[n.source] || "Manual")}</span>
                        </div>
                      </div>
                    </td>
                    <td className="ui-cell-muted">
                      <span className="ui-clamp">{audienceLabel(n.audience, t)}</span>
                    </td>
                    <td>
                      <Badge color={state.color} dot>
                        {t(state.label)}
                      </Badge>
                      {n.error && (
                        <span className="ui-media-meta notif-error" title={n.error}>
                          {n.error}
                        </span>
                      )}
                    </td>
                    <td className="ui-cell-muted notif-result">
                      {n.spread?.enabled && n.spread?.waves > 0 && (
                        <span className="ui-media-meta notif-waves">
                          <i className="ri-timer-line" aria-hidden="true"></i>
                          {t("Wave {{done}} of {{waves}}", { done: n.spread.done || 0, waves: n.spread.waves })}
                          {n.status === "spreading" && n.spread.nextAt ? ` · ${t("next at {{time}}", { time: new Date(n.spread.nextAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}` : ""}
                        </span>
                      )}
                      {["sent", "partial", "failed", "spreading"].includes(n.status) || (n.status === "canceled" && n.spread?.done > 0) ? (
                        n.audience?.type === "all" && n.kind !== "missed_videos" && !n.spread?.enabled ? (
                          <span>{n.stats?.success ? t("Sent to every device") : "—"}</span>
                        ) : (
                          <>
                            <span>
                              <strong>{n.stats?.success ?? 0}</strong> / {n.stats?.targeted ?? 0} {t("delivered")}
                            </span>
                            {n.stats?.invalidTokens > 0 && (
                              <span className="ui-media-meta">
                                {t("{{count}} outdated device(s) removed", { count: n.stats.invalidTokens })}
                              </span>
                            )}
                          </>
                        )
                      ) : (
                        "—"
                      )}
                      {n.stats?.opened > 0 && (
                        <span className="ui-media-meta notif-opened">
                          <i className="ri-cursor-line" aria-hidden="true"></i>
                          {t("{{count}} opened", { count: n.stats.opened })}
                          {n.audience?.type !== "all" && n.stats?.success > 0 ? ` (${Math.round((n.stats.opened / n.stats.success) * 100)} %)` : ""}
                        </span>
                      )}
                    </td>
                    <td className="ui-cell-muted">
                      {n.status === "scheduled" ? (
                        <span title={t("Scheduled")}>
                          <i className="ri-time-line" aria-hidden="true"></i> {formatDate(n.scheduledAt)}
                        </span>
                      ) : (
                        formatDate(n.sentAt || n.createdAt)
                      )}
                    </td>
                    <ActionsCell>
                      {!["missed_videos", "app_update"].includes(n.kind) && (
                        <IconButton icon="ri-file-copy-line" label={t("Duplicate")} onClick={() => onDuplicate(n)} />
                      )}
                      {n.status === "scheduled" || n.status === "spreading" ? (
                        <IconButton
                          icon="ri-close-circle-line"
                          tone="danger"
                          label={n.status === "spreading" ? t("Stop the remaining waves") : t("Cancel sending")}
                          onClick={() => setPending({ action: "cancel", notification: n })}
                        />
                      ) : (
                        n.status !== "sending" && (
                          <IconButton
                            icon="ri-delete-bin-line"
                            tone="danger"
                            label={t("Remove from history")}
                            onClick={() => setPending({ action: "delete", notification: n })}
                          />
                        )
                      )}
                    </ActionsCell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <Pagination
          page={page}
          pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))}
          total={data.total}
          pageSize={data.pageSize}
          onChange={setPage}
        />
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={() => run(pending)}
        danger
        confirmLabel={pending?.action === "cancel" ? (pending.notification.status === "spreading" ? t("Stop the remaining waves") : t("Cancel sending")) : t("Remove")}
      >
        {pending?.action === "cancel" && pending.notification.status === "spreading"
          ? t("Stop the remaining waves? The devices already reached keep the notification; the others will not receive it.")
          : pending?.action === "cancel"
          ? t("Cancel this scheduled notification? It will not be sent.")
          : t("Remove this notification from the history? Users who received it keep it.")}
      </ConfirmDialog>
    </section>
  );
};

export default History;
